import { useState, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"

import "./index.scss"

import { Popup } from "components"
import { UsabilityConfig } from "components/Config"
import Button from "react-bootstrap/Button"
import { FiSettings, FiArrowLeft } from "react-icons/fi"
import { FaPlus } from "react-icons/fa"
import { assets, message } from "state_management"
import useT from "utils/hooks/useT"

// Configuration for instrument usability.
export default function AssetUsabilityConfig({ assetId, onClose }) {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const asset = useSelector((state) => state.asset.graphInfo[assetId])
  const isViewOnly = useSelector((state) => state.auth.isViewOnly)
  const isMember = useSelector((state) => state.auth.isMember)
  const allowTaskTransfer = useSelector((state) => state.teamStatus.allowTaskTransfer)
  const isReadOnly = isViewOnly || (isMember && !allowTaskTransfer)

  // Memoized selector for maintenance items
  const selectMaintenanceItems = useMemo(() => {
    return createSelector(
      [(state) => state.asset.graphInfo[assetId]?.maintenance],
      (maintenance) => {
        if (!maintenance) return [];

        // Handle new format (object with IDs as keys)
        if (!Array.isArray(maintenance)) {
          return Object.entries(maintenance).map(([id, item]) => ({
            ...item,
            id
          }));
        }
        // Handle legacy format (array)
        return maintenance.map(item => ({
          ...item,
          id: item.id
        }));
      }
    );
  }, [assetId]);

  // Use the memoized selector
  const maintenanceItems = useSelector(selectMaintenanceItems)

  // State to track which maintenance item is being edited or if we're creating a new one
  const [editingItem, setEditingItem] = useState(null)
  const [isCreating, setIsCreating] = useState(false)

  // Helper function to format recurrence text
  const formatRecurrence = (item) => {
    // Make unit plural if value is not 1
    const unitText = item.recurrenceValue === 1 ? item.recurrenceUnit : `${item.recurrenceUnit}s`;
    return `${item.recurrenceValue} ${unitText}`;
  }

  // Handle clicking on the settings icon to edit a maintenance item
  const handleEditItem = (item) => {
    setIsCreating(false);
    setEditingItem(item);
  }

  // Handle clicking the add button to create a new maintenance item
  const handleCreateItem = () => {
    setIsCreating(true);
    setEditingItem(null);
  }

  // Handle closing the UsabilityConfig popup
  const handleCloseUsabilityConfig = () => {
    setEditingItem(null);
    setIsCreating(false);
  }

  // Handle marking a maintenance item as done
  const handleMarkAsDone = (item) => {
    dispatch(assets.completeMaintenance(item.id, assetId))
      .then(() => {
        // Refresh asset graph info after completing maintenance
        dispatch(assets.fetchGraphInfo());
        dispatch(message.success(t("maintenanceMarkedAsCompleted", { name: item.name })));
      })
      .catch(() => {
        dispatch(message.error(t("failedToMarkMaintenanceAsCompleted", { name: item.name })));
      });
  }

  // Handle saving changes from the UsabilityConfig
  const handleSaveUsabilityConfig = (maintenanceData) => {
    if (isCreating) {
      // Create a new maintenance item
      dispatch(assets.createMaintenance(assetId, maintenanceData))
        .then(() => {
          // Refresh asset graph info after creating maintenance
          dispatch(assets.fetchGraphInfo());
          dispatch(message.success(t("maintenanceCreatedSuccessfully", { name: maintenanceData.name })));
        })
        .catch(() => {
          dispatch(message.error(t("failedToCreateMaintenance", { name: maintenanceData.name })));
        });
    } else {
      // Update an existing maintenance item
      dispatch(assets.updateMaintenance(maintenanceData.id, maintenanceData))
        .then(() => {
          // Refresh asset graph info after updating maintenance
          dispatch(assets.fetchGraphInfo());
          dispatch(message.success(t("maintenanceUpdatedSuccessfully", { name: maintenanceData.name })));
        })
        .catch(() => {
          dispatch(message.error(t("failedToUpdateMaintenance", { name: maintenanceData.name })));
        });
    }

    setEditingItem(null);
    setIsCreating(false);
  }

  const handleDelete = (item) => {
    dispatch(assets.deleteMaintenance(item.id, assetId))
      .then(() => {
        // Refresh asset graph info after deleting maintenance
        dispatch(assets.fetchGraphInfo());
        dispatch(message.success(t("maintenanceDeletedSuccessfully", { name: item.name })));
      })
      .catch(() => {
        dispatch(message.error(t("failedToDeleteMaintenance", { name: item.name })));
      });

    setEditingItem(null);
    setIsCreating(false);
  }

  // If we're creating or editing an item, show the UsabilityConfig component
  if (isCreating || editingItem) {
    return (
      <Popup nonScrollable onCancel={onClose}>
        <Button className="go-back-button" variant="secondary" onClick={handleCloseUsabilityConfig}>
          <FiArrowLeft />
        </Button>
        <UsabilityConfig
          maintenanceItem={editingItem}
          assetName={asset?.name}
          onCancel={handleCloseUsabilityConfig}
          onSave={handleSaveUsabilityConfig}
          onDelete={handleDelete}
          isCreating={isCreating}
          readOnly={isReadOnly}
        />
      </Popup>
    );
  }

  return (
    <Popup title={asset?.name} nonScrollable onCancel={onClose}>
      <div className="usability-config">
        <table className="maintenance-table">
          <thead>
            <tr>
              <th>{t("name")}</th>
              <th>{t("endDate")}</th>
              <th>{t("recurrence")}</th>
              <th></th> {/* Column for Done button */}
              <th></th> {/* Column for settings icon */}
            </tr>
          </thead>
          <tbody>
            {maintenanceItems.map((item) => {
              return (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>{item.endDate}</td>
                  <td>{formatRecurrence(item)}</td>
                  {!isReadOnly && (
                    <td className="done-cell">
                      <Button
                        className="done-button"
                        variant="primary"
                        onClick={() => handleMarkAsDone(item)}
                      >
                        {t("done")}
                      </Button>
                    </td>
                  )}
                  {!isReadOnly && (
                    <td>
                      <FiSettings className="settings-icon" onClick={() => handleEditItem(item)} />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>

        {!isReadOnly && (
          <div className="add-maintenance">
            <Button
              className="add-button"
              variant="primary"
              onClick={handleCreateItem}
            >
              <FaPlus /> {t("addMaintenance")}
            </Button>
          </div>
        )}
      </div>
    </Popup>
  );
}
